<template>
  <div class="left padding">
    <div class="whitePanel marginBottom">
      <div class="grid">
        <div class="col-12 md:col-4">
          <p class="blueText marginB5Px">
            {{ $t("misconsultas.header.identificador") }}
          </p>
          <InputText
            v-model="filters['token']"
            class="input"
            id="token"
            type="text"
            :placeholder="$t('misconsultas.placeholders.identificador')"
          />
        </div>
        <div class="col-12 md:col-4">
          <p class="blueText marginB5Px">{{ $t("misconsultas.header.nif") }}</p>
          <InputText
            v-model="filters['nifDeudor']"
            class="input"
            id="documentoIdentidad"
            type="tel"
            :placeholder="$t('misconsultas.placeholders.nif')"
          />
        </div>
        <div class="col-12 md:col-4">
          <p class="blueText marginB5Px">
            {{ $t("misconsultas.header.estado") }}
          </p>
          <Dropdown
            v-model="filters['estado']"
            :options="estados"
            optionValue="code"
            optionLabel="name"
            :placeholder="$t('misconsultas.placeholders.estado')"
            class="input"
          />
        </div>
        <div class="col-12 md:col-4">
          <p class="blueText marginB5Px">
            {{ $t("misconsultas.header.tipo") }}
          </p>
          <Dropdown
            v-model="filters['tipo']"
            :options="tipos"
            optionValue="code"
            optionLabel="name"
            :placeholder="$t('misconsultas.placeholders.tipo')"
            class="input"
          />
        </div>
        <div class="col-12">
          <div class="flex">
            <Button
              @click="clearFields()"
              :label="$t('common.buttons.clear')"
              class="buttonStyle rightAlign lightBlueBG"
            />
          </div>
        </div>
      </div>
    </div>
    <DataTable
      :value="searches.items"
      :loading="loading"
      :lazy="true"
      :paginator="true"
      :rows="tamPagina"
      :totalRecords="searches.totalElements"
      :filters.sync="filters"
      class="p-datatable-responsive"
      @sort="customSort($event)"
      @page="onPage($event)"
    >
      <template #empty>
        {{ $t("misconsultas.datatable.noConsultas") }}
      </template>

      <Column
        field="Identificador"
        :header="$t('misconsultas.header.identificador')"
        :sortable="false"
        :headerStyle="headerStyle"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("misconsultas.label.identificador")
          }}</span>
          {{ slotProps.data.token }}
        </template>
      </Column>
      <Column
        field="fechaAlta"
        :header="$t('misconsultas.header.fecha')"
        :sortable="true"
        :headerStyle="headerStyle"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("misconsultas.label.fecha")
          }}</span>
          {{ getFormattedDateShort(slotProps.data.fechaAlta) }}
        </template>
      </Column>
      <Column
        field="NIF"
        :header="$t('misconsultas.header.nif')"
        :sortable="false"
        :headerStyle="headerStyle"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("misconsultas.label.nif") }}</span>
          {{ slotProps.data.idLegalDeudor }}
        </template>
      </Column>
      <Column
        field="Tipo"
        :header="$t('misconsultas.header.tipo')"
        :sortable="false"
        :headerStyle="headerStyle"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("misconsultas.label.tipo")
          }}</span>
          {{ slotProps.data.literalSolicitudTipo.valor }}
        </template>
      </Column>
      <Column
        field="Estado"
        :header="$t('misconsultas.header.estado')"
        :sortable="false"
        :headerStyle="headerStyle"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("misconsultas.label.estado")
          }}</span>
          {{ slotProps.data.literalSolicitudEstado.valor }}
        </template>
      </Column>
      <Column
        field="Informe"
        :header="$t('misconsultas.header.informe')"
        :sortable="false"
        :headerStyle="headerStyle"
        bodyStyle="text-align:center"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("misconsultas.label.informe")
          }}</span>
          {{ slotProps.data.literalSolicitudEstado.valor ? "" : "" }}
          <Button
            v-if="!canBeDownloaded(slotProps.data.estado)"
            :disabled="true"
            class="centerButton"
            :header="$t('misconsultas.header.descargar')"
            icon="pi pi-chevron-down"
            iconPos="left"
          />
          <Button
            v-if="canBeDownloaded(slotProps.data.estado)"
            :disabled="isSubmiting"
            @click="getListaDocumentosMinio(typeFileFM, slotProps.data.token)"
            class="centerButton"
            :header="$t('misconsultas.header.descargar')"
            icon="pi pi-chevron-down"
            iconPos="left"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { QUERY_LISTA_SOLICITUDES } from "@/graphQLQueries/ConsultasQueries";
import { GET_LIST_FILES_BY_TYPE_CLAVE } from "@/graphQLQueries/DocStoreQueries";

import {
  ESTADO_SOLICITUD_FM,
  TIPO_EMPRESA_PARTICULAR_FM,
  TIPOS_FICHEROS,
} from "@/resources/constants";

import DatesMixin from "@/utils/dates";
import DownloadsMixin from "@/utils/downloads";
import MessagesMixin from "@/utils/messages";

export default {
  name: "ConsultasMain",
  mixins: [DatesMixin, DownloadsMixin, MessagesMixin],
  data() {
    return {
      estados: [
        {
          name: this.$i18n.t("misconsultas.dropdowns.estado.pendiente"),
          code: ESTADO_SOLICITUD_FM.SOLICITUD_ESTADO_PENDIENTE,
        },
        {
          name: this.$i18n.t("misconsultas.dropdowns.estado.aceptada"),
          code: ESTADO_SOLICITUD_FM.SOLICITUD_ESTADO_ACEPTADA,
        },
        {
          name: this.$i18n.t("misconsultas.dropdowns.estado.rechazada"),
          code: ESTADO_SOLICITUD_FM.SOLICITUD_ESTADO_RECHAZADA,
        },
      ],
      tipos: [
        {
          name: this.$i18n.t("misconsultas.dropdowns.tipo.particular"),
          code: TIPO_EMPRESA_PARTICULAR_FM.PARTICULAR,
        },
        {
          name: this.$i18n.t("misconsultas.dropdowns.tipo.empresa"),
          code: TIPO_EMPRESA_PARTICULAR_FM.EMPRESA,
        },
      ],
      sort: ["-fechaAlta"],
      numPagina: 0,
      tamPagina: 10,
      loading: true,
      filters: {},
      searches: {},
      headerStyle: "color: white; text-align: center",
      statusAccepted: ESTADO_SOLICITUD_FM.SOLICITUD_ESTADO_ACEPTADA,
      typeFileFM: TIPOS_FICHEROS.FICH_MOROSIDAD_PDF,
      isSubmiting: false,
    };
  },
  apollo: {
    searches: {
      query: QUERY_LISTA_SOLICITUDES,
      variables() {
        return {
          criteria: {
            filter: this.backendFilters,
            page: this.numPagina,
            size: this.tamPagina,
            sort: this.sort,
          },
          idioma: this.$i18n.locale,
        };
      },
      fetchPolicy: "no-cache",
      update(data) {
        const searches = data.listaSolicitudes;
        this.loading = false;

        return {
          items: searches.results,
          totalElements: searches.totalElements,
        };
      },
    },
  },
  computed: {
    backendFilters() {
      let filters2 = [
        `solicitante.cliente.uuid~uuid=${this.$store.state.clienteUuid}`,
      ];
      for (let [key, value] of Object.entries(this.filters)) {
        if (value != null && value != undefined && `${value}`) {
          filters2.push(`${key}~like=${value}`);
        }
      }
      return filters2;
    },
  },
  methods: {
    clearFields() {
      this.filters = {};
    },
    search() {
      this.loading = true;

      this.$apollo.queries.searches.refetch();
    },
    onPage(event) {
      this.numPagina = event.page;
    },
    customSort(event) {
      var sortField = event.sortField;
      var sortOrder = event.sortOrder == -1 ? "-" : "";
      var sortToApply = sortOrder + sortField;
      var sorts = [sortToApply];
      this.sort = sorts;
      this.search();
    },
    getListaDocumentosMinio(type, clave) {
      this.isSubmiting = true;
      this.$apollo
        .query({
          query: GET_LIST_FILES_BY_TYPE_CLAVE,
          variables: {
            type: type,
            clave: clave,
          },
          fetchPolicy: "no-cache",
        })
        .then((data) => {
          let listaDocumentosMinio = data.data.getListFileByTypeAndClave;
          if (
            listaDocumentosMinio != null &&
            listaDocumentosMinio != undefined &&
            listaDocumentosMinio.length > 0
          ) {
            let documentoMinio = listaDocumentosMinio[0];
            this.download(
              documentoMinio.filename,
              documentoMinio.base64,
              document
            );
            this.setMessages(
              "success",
              this.$t("misconsultas.messages.success.descargaFichero"),
              3000
            );
          } else {
            this.setMessages(
              "info",
              this.$t("misconsultas.messages.info.descargaFichero"),
              3000
            );
          }
          this.disableSubmit();
        })
        .catch(() => {
          this.setMessages(
            "info",
            this.$t("misconsultas.messages.info.descargaFichero"),
            3000
          );
          this.disableSubmit();
        });
    },
    canBeDownloaded(statusRequest) {
      return statusRequest == this.statusAccepted;
    },
    disableSubmit() {
      let self = this;
      setTimeout(function () {
        self.isSubmiting = false;
      }, 3000);
    },
  },
};
</script>

<style lang="scss">
.p-dropdown-items-wrapper {
  ul {
    display: inline !important;
    margin-bottom: 0 !important;
  }
  li {
    margin-left: 0 !important;
  }
}
</style>

<style lang="scss" scoped>
.padding {
  padding: 2%;
}

.botonFiltrar {
  margin-right: 5px;
}

.column-title {
  color: white;
}

// RESPONSIVE
.p-datatable-responsive .p-datatable-tbody > tr > td .column-title {
  display: none;
}

@media screen and (max-width: 640px) {
  .column-title {
    color: black;
  }
}

@media screen and (max-width: 960px) {
  .centerButton {
    width: 100% !important;
  }
}
@media screen and (max-width: 960px) {
  .centerButton {
    width: 100% !important;
  }
}
</style>